import {
  AfterViewInit,
  Directive,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  inject,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, filter } from 'rxjs';

/**
 * Sliding pill indicator for Workshop & Site Visit orientation tabs.
 * Host should contain `.tab-indicator` and tab links/buttons with `[data-tab]`.
 */
@Directive({
  selector: '[appOrientationSlidingTabs]',
  standalone: false,
})
export class OrientationSlidingTabsDirective implements AfterViewInit, OnDestroy {
  private readonly host = inject(ElementRef);
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private routeSub?: Subscription;
  private ready = false;

  private get el(): HTMLElement {
    return this.host.nativeElement as HTMLElement;
  }

  ngAfterViewInit(): void {
    const el = this.el;
    el.classList.add('orientation-tabs');

    this.zone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        this.syncFromUrl(false);
        requestAnimationFrame(() => {
          this.ready = true;
          el.classList.add('tabs-animated');
          this.syncFromUrl(true);
        });
      });
    });

    this.routeSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => {
        this.syncFromUrl(true);
        this.bumpContentEnter();
      });

    el.addEventListener('click', this.onClick, true);
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
    this.el.removeEventListener('click', this.onClick, true);
  }

  @HostListener('window:resize')
  onResize(): void {
    this.syncFromUrl(false);
  }

  private bumpContentEnter(): void {
    const parent = this.el.parentElement;
    const content = parent?.querySelector('.tab-content') as HTMLElement | null;
    if (!content) {
      return;
    }
    content.classList.remove('tab-content-enter');
    void content.offsetWidth;
    content.classList.add('tab-content-enter');
  }

  private onClick = (event: Event): void => {
    const target = (event.target as HTMLElement | null)?.closest?.(
      '[data-tab]',
    ) as HTMLElement | null;
    if (!target) {
      return;
    }
    const key = target.getAttribute('data-tab');
    if (key) {
      this.moveTo(key, true);
    }
  };

  private syncFromUrl(animate: boolean): void {
    const url = this.router.url;
    const tabs = Array.from(this.el.querySelectorAll('[data-tab]')) as HTMLElement[];
    const match =
      tabs.find((t) => url.includes(`/${t.getAttribute('data-tab')}`)) ??
      tabs.find((t) => url.endsWith(t.getAttribute('data-tab') || '')) ??
      tabs[0];
    if (match) {
      this.moveTo(match.getAttribute('data-tab') || '', animate);
    }
  }

  private moveTo(key: string, animate: boolean): void {
    const container = this.el;
    const indicator = container.querySelector('.tab-indicator') as HTMLElement | null;
    const tab = container.querySelector(`[data-tab="${key}"]`) as HTMLElement | null;
    if (!indicator || !tab) {
      return;
    }

    if (!animate || !this.ready) {
      indicator.style.transition = 'none';
    } else {
      indicator.style.transition = '';
    }

    indicator.style.width = `${tab.offsetWidth}px`;
    indicator.style.transform = `translateX(${tab.offsetLeft}px)`;
    indicator.classList.add('visible');

    tabsActiveClass(container, key);

    if (!animate || !this.ready) {
      void indicator.offsetWidth;
      indicator.style.transition = '';
    }
  }
}

function tabsActiveClass(container: HTMLElement, key: string): void {
  Array.from(container.querySelectorAll('[data-tab]')).forEach((node) => {
    const el = node as HTMLElement;
    el.classList.toggle('active', el.getAttribute('data-tab') === key);
  });
}
